/**
 * Cevi Budget - JavaScript Interop for Blazor
 * 
 * All functions are exposed via the `window.app` namespace for Blazor JS interop.
 * Usage in C#: await JSRuntime.InvokeVoidAsync("app.functionName", args);
 * 
 * Some utilities use their own namespace (focusTrap) for logical grouping.
 */

// ============================================================================
// FILE DOWNLOADS
// ============================================================================

async function downloadFileFromStream(fileName, contentStreamReference) {
    const arrayBuffer = await contentStreamReference.arrayBuffer();
    const blob = new Blob([arrayBuffer]);
    const url = URL.createObjectURL(blob);
    const anchorElement = document.createElement('a');
    anchorElement.href = url;
    anchorElement.download = fileName ?? '';
    anchorElement.click();
    anchorElement.remove();
    URL.revokeObjectURL(url);
}

// ============================================================================
// SCRIPT LOADING
// ============================================================================

// Scripts already requested, so that concurrent callers share one <script> tag
// and one network request: url -> Promise
const loadedScripts = new Map();

/**
 * Adds a script to the page and resolves once it has run. Loading the same URL
 * again returns the original promise rather than adding a second tag.
 *
 * Used for vendor scripts that only a few features need, to keep them out of
 * the document head where every page would pay for them.
 *
 * @param {string} url
 * @returns {Promise<void>}
 */
function ensureScript(url) {
    if (loadedScripts.has(url)) {
        return loadedScripts.get(url);
    }

    const loading = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.addEventListener('load', () => resolve());
        script.addEventListener('error', () => {
            // Allow a later attempt to retry instead of reusing the rejection.
            loadedScripts.delete(url);
            reject(new Error(`Failed to load script ${url}`));
        });
        document.head.append(script);
    });

    loadedScripts.set(url, loading);
    return loading;
}

// ============================================================================
// DOM UTILITIES
// ============================================================================

function getDimensions() {
    return {
        width: window.innerWidth,
        height: window.innerHeight
    };
}

function getBoundingClientRect(element) {
    return element.getBoundingClientRect();
}

// ============================================================================
// SCROLLING
// ============================================================================

/**
 * Horizontal scroll to child element inside container.
 * @returns {boolean} True if user scrolled to end
 */
function scrollToElement(containerId, childId) {
    const containerElement = document.getElementById(containerId);
    const childElement = document.getElementById(childId);

    if (!containerElement || !childElement) return false;

    containerElement.scrollTo({
        left: childElement.offsetLeft,
        behavior: "smooth"
    });
    return containerElement.offsetWidth + childElement.offsetLeft + 1 >= containerElement.scrollWidth;
}

function scrollToElementWhenLoaded(elementId) {
    const observer = new MutationObserver((_, obs) => {
        const el = document.getElementById(elementId);
        if (el) {
            el.scrollIntoView({ behavior: "smooth" });
            obs.disconnect();
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });
}

// ============================================================================
// GLOBAL EVENT HANDLERS
// ============================================================================

// Prevent accidental file drops outside designated areas
window.addEventListener("dragover", (e) => {
    e.stopImmediatePropagation();
    e.preventDefault();
}, false);

window.addEventListener("drop", (e) => {
    if (e.dataTransfer?.files.length > 0) {
        if (e.target && !e.target.className?.includes("c-file-uploader")) {
            e.stopImmediatePropagation();
            e.preventDefault();
        }
    }
}, false);

// Prevent scrolling from changing the value of a focused number input:
// blur it so the wheel scrolls the page instead of stepping the value.
// Passive because we never preventDefault, so scrolling stays off the JS critical path.
document.addEventListener("wheel", (e) => {
    const el = document.activeElement;
    if (el instanceof HTMLInputElement && el.type === "number" && el.contains(e.target)) {
        el.blur();
    }
}, { passive: true });

// ============================================================================
// FOCUS TRAP - For modal dialogs
// ============================================================================

window.focusTrap = {
    trap(elementId) {
        const element = document.getElementById(elementId);
        if (!element) return;

        function getFocusableElements() {
            return Array.from(element.querySelectorAll(
                'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )).filter(el => !el.hasAttribute('disabled') && el.getAttribute('type') !== 'hidden');
        }

        function handle(e) {
            if (e.key !== 'Tab') return;

            const focusable = getFocusableElements();
            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }

        element.addEventListener('keydown', handle);
        element._trapHandler = handle;

        const initialElements = getFocusableElements();
        if (initialElements.length) initialElements[0].focus();

        const observer = new MutationObserver(() => {
            if (!element.contains(document.activeElement)) {
                const focusable = getFocusableElements();
                if (focusable.length) focusable[0].focus();
            }
        });
        observer.observe(element, { childList: true, subtree: true });
        element._observer = observer;
    },

    release(elementId) {
        const element = document.getElementById(elementId);
        if (!element) return;

        if (element._trapHandler) {
            element.removeEventListener('keydown', element._trapHandler);
            delete element._trapHandler;
        }
        if (element._observer) {
            element._observer.disconnect();
            delete element._observer;
        }
    }
};

// ============================================================================
// EXPOSE TO WINDOW FOR BLAZOR JS INTEROP
// ============================================================================

// Main app namespace
window.app = {
    downloadFileFromStream,
    ensureScript,
    getDimensions,
    getBoundingClientRect,
    scrollToElement,
    scrollToElementWhenLoaded
};